import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "aexents_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image_url: product.image_url,
          size_ml: product.size_ml,
          quantity: qty,
        },
      ];
    });
    setOpen(true);
  };

  const remove = (id) => setItems((p) => p.filter((i) => i.product_id !== id));
  const setQty = (id, q) =>
    setItems((p) =>
      p.map((i) => (i.product_id === id ? { ...i, quantity: Math.max(1, q) } : i))
    );
  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    // Layer Your Signature — mirror backend tiered bundle: 15% (duo) / 22% (trio)
    const PALETTE = new Set(["Blæk", "Vétiver", "Ivoire"]);
    const paletteLines = items.filter((i) => PALETTE.has(i.name));
    const distinctPalette = new Set(paletteLines.map((i) => i.name));
    const paletteLinesTotal = paletteLines.reduce((s, i) => s + i.price * i.quantity, 0);
    let duoRate = 0;
    if (distinctPalette.size >= 3) duoRate = 0.22;
    else if (distinctPalette.size >= 2) duoRate = 0.15;
    const duoDiscount = Math.round(paletteLinesTotal * duoRate * 100) / 100;
    const bundleTier = distinctPalette.size >= 3 ? "trio" : distinctPalette.size >= 2 ? "duo" : null;
    const discountedSubtotal = subtotal - duoDiscount;
    const shipping = discountedSubtotal >= 50 || subtotal === 0 ? 0 : 5.95;
    // Product prices are VAT-inclusive (UK 20%). Extract the VAT portion for
    // display but do NOT add it on top — the total is subtotal + shipping.
    const tax = Math.round((discountedSubtotal - discountedSubtotal / 1.20) * 100) / 100;
    const total = Math.round((discountedSubtotal + shipping) * 100) / 100;
    const count = items.reduce((s, i) => s + i.quantity, 0);
    return { subtotal, duoDiscount, bundleTier, shipping, tax, total, count };
  }, [items]);

  return (
    <CartContext.Provider
      value={{ items, add, remove, setQty, clear, totals, open, setOpen }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
